import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { AuthUser } from '../types/index.js';
import { auth as authApi, ApiError } from '../api/client.js';

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  /** true when user has 2FA enabled but hasn't validated this session yet */
  needsTotpValidation: boolean;
  checkSession: () => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: AuthUser) => void;
  setNeedsTotpValidation: (v: boolean) => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsTotpValidation, setNeedsTotpValidation] = useState(false);

  const checkSession = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const u = await authApi.me();
      console.log('[auth] session valid, user:', u.discordUsername, 'role:', u.role, 'whitelisted:', u.whitelisted);
      setUser(u);
      // if they have totp enabled, they need to validate before accessing the app
      // the backend session data has needsTotp but we check totpEnabled as the flag
      if (u.totpEnabled) {
        setNeedsTotpValidation(true);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        console.log('[auth] no active session');
        setUser(null);
      } else {
        console.error('[auth] session check failed:', err);
        setError('Failed to check session');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
      console.log('[auth] logged out');
    } catch (err) {
      console.error('[auth] logout error:', err);
    }
    setUser(null);
    setNeedsTotpValidation(false);
  }, []);

  useEffect(() => {
    void checkSession();
  }, [checkSession]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        needsTotpValidation,
        checkSession,
        logout,
        setUser,
        setNeedsTotpValidation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
